/**
 * ExpenseManager - Income & Expense Tracking (FIXED)
 * ThePedigreeApp
 */

import { useState, useEffect, useRef } from 'react';
import { Plus, Search, Download, Upload, Edit2, Trash2, DollarSign, TrendingUp, TrendingDown, FileSpreadsheet, X, Save } from 'lucide-react';
import { Card, Button, Badge, Alert } from '../shared/UIComponents';
import { cn } from '../../utils/helpers';

const EXP_CATS = [
  { value: 'food', label: 'Cat Food', color: 'bg-orange-100 text-orange-700' },
  { value: 'vet', label: 'Veterinary', color: 'bg-red-100 text-red-700' },
  { value: 'supplies', label: 'Supplies', color: 'bg-blue-100 text-blue-700' },
  { value: 'registration', label: 'Registration', color: 'bg-purple-100 text-purple-700' },
  { value: 'equipment', label: 'Equipment', color: 'bg-amber-100 text-amber-700' },
  { value: 'other', label: 'Other', color: 'bg-slate-100 text-slate-700' },
];

const INC_CATS = [
  { value: 'kitten_sale', label: 'Kitten Sale', color: 'bg-green-100 text-green-700' },
  { value: 'stud_fee', label: 'Stud Fee', color: 'bg-blue-100 text-blue-700' },
  { value: 'deposit', label: 'Deposit', color: 'bg-teal-100 text-teal-700' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' },
];

const TxnModal = ({ isOpen, onClose, onSave, txn, type }) => {
  const init = { date: new Date().toISOString().split('T')[0], description: '', amount: '', category: type === 'expense' ? 'other' : 'kitten_sale', vendor: '', reference: '', notes: '' };
  const [form, setForm] = useState(init);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const cats = type === 'expense' ? EXP_CATS : INC_CATS;

  useEffect(() => {
    if (isOpen) {
      setForm(txn ? { ...init, ...txn, amount: txn.amount?.toString() || '' } : { ...init, category: type === 'expense' ? 'other' : 'kitten_sale' });
      setErrors({});
    }
  }, [isOpen, txn, type]);

  if (!isOpen) return null;

  const validate = () => {
    const e = {};
    if (!form.date) e.date = 'Required';
    if (!form.description?.trim()) e.description = 'Required';
    if (!form.amount || Number(form.amount) <= 0) e.amount = 'Required > 0';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave({ ...form, amount: Number(form.amount), type, id: txn?.id || `txn-${Date.now()}`, createdAt: txn?.createdAt || new Date().toISOString() });
      onClose();
    } catch (err) {
      setErrors({ submit: 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (f, v) => {
    setForm(p => ({ ...p, [f]: v }));
    if (errors[f]) setErrors(p => ({ ...p, [f]: null }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-xl font-bold">{txn ? 'Edit' : 'Add'} {type === 'expense' ? 'Expense' : 'Income'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {errors.submit && <Alert type="error">{errors.submit}</Alert>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Date *</label>
              <input type="date" value={form.date} onChange={(e) => handleChange('date', e.target.value)} className={cn("w-full px-4 py-2 border rounded-xl", errors.date ? "border-red-500" : "border-gray-300")} />
              {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Amount (R) *</label>
              <input type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => handleChange('amount', e.target.value)} placeholder="0.00" className={cn("w-full px-4 py-2 border rounded-xl", errors.amount ? "border-red-500" : "border-gray-300")} />
              {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description *</label>
            <input type="text" value={form.description} onChange={(e) => handleChange('description', e.target.value)} placeholder={type === 'expense' ? "e.g. Cat food" : "e.g. Kitten deposit"} className={cn("w-full px-4 py-2 border rounded-xl", errors.description ? "border-red-500" : "border-gray-300")} />
            {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Category</label>
            <select value={form.category} onChange={(e) => handleChange('category', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-xl">
              {cats.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          {type === 'expense' && (
            <div>
              <label className="block text-sm font-medium mb-1">Vendor</label>
              <input type="text" value={form.vendor} onChange={(e) => handleChange('vendor', e.target.value)} placeholder="e.g. Pet Store" className="w-full px-4 py-2 border border-gray-300 rounded-xl" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Reference #</label>
            <input type="text" value={form.reference} onChange={(e) => handleChange('reference', e.target.value)} placeholder="Invoice/receipt #" className="w-full px-4 py-2 border border-gray-300 rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} rows={2} placeholder="Notes..." className="w-full px-4 py-2 border border-gray-300 rounded-xl resize-none" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={saving}>Cancel</Button>
            <Button type="submit" className="flex-1" disabled={saving}>{saving ? 'Saving...' : <><Save className="w-4 h-4" /> Save</>}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ExpenseManager = () => {
  const [txns, setTxns] = useState([
    { id: '1', date: '2024-02-01', description: 'Royal Canin Food', amount: 850, category: 'food', type: 'expense', vendor: 'Pet Store' },
    { id: '2', date: '2024-02-05', description: 'Vet checkup', amount: 650, category: 'vet', type: 'expense', vendor: 'CT Vet' },
    { id: '3', date: '2024-02-10', description: 'Kitten sale - Luna', amount: 15000, category: 'kitten_sale', type: 'income' },
    { id: '4', date: '2024-02-15', description: 'Deposit - Bella kitten', amount: 3000, category: 'deposit', type: 'income' },
  ]);
  const [showTxn, setShowTxn] = useState(false);
  const [txnType, setTxnType] = useState('expense');
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const income = txns.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
  const expense = txns.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
  const profit = income - expense;

  const filtered = txns.filter(t => {
    if (filter !== 'all' && t.type !== filter) return false;
    if (search && !t.description.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).sort((a, b) => new Date(b.date) - new Date(a.date));

  const handleSave = async (t) => {
    if (editing) setTxns(p => p.map(x => x.id === t.id ? t : x));
    else setTxns(p => [t, ...p]);
    setEditing(null);
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this transaction?')) {
      setTxns(p => p.filter(t => t.id !== id));
    }
  };

  const getCat = (cat, type) => {
    const cats = type === 'expense' ? EXP_CATS : INC_CATS;
    return cats.find(c => c.value === cat) || { label: cat, color: 'bg-gray-100 text-gray-700' };
  };

  const exportCSV = () => {
    const headers = 'Date,Description,Amount,Category,Type,Vendor,Reference,Notes';
    const rows = txns.map(t => `${t.date},"${t.description}",${t.amount},${t.category},${t.type},"${t.vendor || ''}","${t.reference || ''}","${t.notes || ''}"`);
    const blob = new Blob([[headers, ...rows].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900">Finances</h1>
          <p className="text-gray-500">Track income and expenses</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportCSV}><Download className="w-4 h-4" /> Export</Button>
          <Button className="bg-green-600 hover:bg-green-700" onClick={() => { setTxnType('income'); setEditing(null); setShowTxn(true); }}><Plus className="w-4 h-4" /> Income</Button>
          <Button onClick={() => { setTxnType('expense'); setEditing(null); setShowTxn(true); }}><Plus className="w-4 h-4" /> Expense</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center"><TrendingUp className="w-6 h-6 text-green-600" /></div>
            <div><p className="text-sm text-gray-500">Total Income</p><p className="text-2xl font-bold text-green-600">R{income.toLocaleString()}</p></div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center"><TrendingDown className="w-6 h-6 text-red-600" /></div>
            <div><p className="text-sm text-gray-500">Total Expenses</p><p className="text-2xl font-bold text-red-600">R{expense.toLocaleString()}</p></div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center', profit >= 0 ? 'bg-pedigree-100' : 'bg-red-100')}>
              <DollarSign className={cn('w-6 h-6', profit >= 0 ? 'text-pedigree-600' : 'text-red-600')} />
            </div>
            <div><p className="text-sm text-gray-500">Net Profit</p><p className={cn('text-2xl font-bold', profit >= 0 ? 'text-pedigree-600' : 'text-red-600')}>R{profit.toLocaleString()}</p></div>
          </div>
        </Card>
      </div>

      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl" />
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-xl">
            <option value="all">All</option>
            <option value="income">Income</option>
            <option value="expense">Expenses</option>
          </select>
        </div>
      </Card>

      <Card>
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Transactions</h3>
            <p className="text-gray-500 mb-4">Add your first income or expense.</p>
            <div className="flex justify-center gap-2">
              <Button onClick={() => { setTxnType('income'); setShowTxn(true); }}><Plus className="w-4 h-4" /> Income</Button>
              <Button variant="outline" onClick={() => { setTxnType('expense'); setShowTxn(true); }}><Plus className="w-4 h-4" /> Expense</Button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Description</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Category</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Amount</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((t) => {
                  const cat = getCat(t.category, t.type);
                  return (
                    <tr key={t.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600">{new Date(t.date).toLocaleDateString('en-ZA')}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{t.description}</p>
                        {t.vendor && <p className="text-xs text-gray-500">{t.vendor}</p>}
                      </td>
                      <td className="px-4 py-3"><Badge className={cat.color}>{cat.label}</Badge></td>
                      <td className={cn('px-4 py-3 text-right font-medium', t.type === 'income' ? 'text-green-600' : 'text-red-600')}>
                        {t.type === 'income' ? '+' : '-'}R{t.amount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setEditing(t); setTxnType(t.type); setShowTxn(true); }}><Edit2 className="w-4 h-4" /></Button>
                          <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(t.id)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <TxnModal isOpen={showTxn} onClose={() => { setShowTxn(false); setEditing(null); }} onSave={handleSave} txn={editing} type={txnType} />
    </div>
  );
};

export default ExpenseManager;
